import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { AlertCircleIcon, ArrowLeftIcon, ArrowUpRightIcon, CalendarIcon, CheckCircleIcon, CircleSlash, ClockIcon, Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { AssignmentBuilderQuizCard } from '@/components/features/assignments/builder/AssignmentBuilderQuizCard';
import { AttachmentCard } from '@/components/features/files/attachment-card';
import { FilesMultipleSelector } from '@/components/features/files/file-multiple-selector';
import QuizStartAlertDialog from '@/components/features/quizzes/QuizStartAlertDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/dialog';
import { EmptyState, EmptyStateCircles, EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Skeleton } from '@/components/ui/skeleton';
import { Tiptap, TiptapToolbar } from '@/components/ui/tiptap-editor';
import { useAssignmentResolveForm } from '@/hooks/assignment';
import { postExtensionSet, simpleExtensionSet } from '@/lib/tiptap-extensions-sets';

export default function AssignmentResolvePage() {
  const navigate = useNavigate();
  const { assignmentId, assignment, assignmentQuery, form, attachments, setAttachments, canSubmit, isQuizAssignment, usedAttempts, onSubmit, onInvalid, isPending, isLoadingRemote, submitError } =
    useAssignmentResolveForm();

  const [startDialog, setStartDialog] = useState<[boolean, string | null]>([false, null]);

  const bodyValue = useWatch({ control: form.control, name: 'body' }) ?? '';

  if (assignmentId === undefined) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>Nieprawidłowy adres</EmptyStateTitle>
            <EmptyStateDescription>Brak identyfikatora zadania.</EmptyStateDescription>
          </EmptyStateContent>
          <EmptyStateFooter>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              <ArrowLeftIcon />
              Powrót
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  if (assignmentQuery.isError) {
    return (
      <div className="base-direction-col">
        <EmptyState>
          <EmptyStateCircles className="z-0" />
          <FeaturedIcon size="lg" className="z-10">
            <CircleSlash />
          </FeaturedIcon>
          <EmptyStateContent>
            <EmptyStateTitle>{assignmentQuery.errorMessage.title}</EmptyStateTitle>
            <EmptyStateDescription>{assignmentQuery.errorMessage.detail}</EmptyStateDescription>
          </EmptyStateContent>
          <EmptyStateFooter>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              <ArrowLeftIcon />
              Powrót
            </Button>
          </EmptyStateFooter>
        </EmptyState>
      </div>
    );
  }

  if (isLoadingRemote || !assignment) {
    return (
      <div className="base-direction-col h-full overflow-hidden">
        <div className="bg-background viewport-height relative flex w-full flex-col overflow-hidden rounded-2xl border">
          <div className="border-border-primary border-b p-4">
            <Skeleton className="mb-2 h-7 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="space-y-4 p-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const isLocked = !isQuizAssignment && !canSubmit;
  const podejsciaText = assignment.maxAttempts === 0 ? `Podejścia: ${usedAttempts}` : `Podejścia: ${usedAttempts} / ${assignment.maxAttempts}`;
  const quiz = assignment.quiz;
  // After completing the quiz, return to the assignment view.
  const backTo = `/assignments/${assignmentId}`;

  return (
    <div className="base-direction-col h-full overflow-hidden">
      <div className="bg-background viewport-height relative flex w-full flex-col overflow-hidden rounded-2xl border">
        {quiz && <QuizStartAlertDialog quiz={quiz} state={startDialog} onStateChange={setStartDialog} backTo={backTo} />}
        <Form {...form}>
          <form noValidate id="assignment-resolve-form" onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="relative flex grow flex-col overflow-auto">
            {isPending && (
              <div className="bg-background/50 absolute inset-0 z-20 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-[2px]">
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
                <h5 className="font-medium">Zapisywanie...</h5>
              </div>
            )}

            {/* Header */}
            <header className="border-border-primary flex flex-col gap-3 border-b p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex flex-col">
                  <h1 className="text-2xl font-semibold">{assignment.name}</h1>
                  <p className="text-muted-foreground text-sm">{isQuizAssignment ? 'Rozwiąż quiz aby ukończyć zadanie' : 'Prześlij nowe podejście'}</p>
                </div>
                {!isQuizAssignment && (
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button type="button" variant="primary" disabled={isPending || isLocked}>
                          <CheckCircleIcon />
                          Prześlij
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="sm:max-w-96">
                        <AlertDialogBody>
                          <AlertDialogHeader>
                            <FeaturedIcon size="lg">
                              <CheckCircleIcon />
                            </FeaturedIcon>
                            <div className="flex min-w-0 grow flex-col gap-0.5">
                              <AlertDialogTitle>Prześlij podejście</AlertDialogTitle>
                              <AlertDialogDescription className="text-muted-foreground text-xs">
                                Czy na pewno chcesz przesłać swoją odpowiedź? Zostanie ona zapisana jako kolejne podejście.
                              </AlertDialogDescription>
                            </div>
                          </AlertDialogHeader>
                        </AlertDialogBody>
                        <AlertDialogFooter className="border-border-primary bg-accent dark:bg-card border-t">
                          <AlertDialogCancel>Anuluj</AlertDialogCancel>
                          <AlertDialogAction type="submit" form="assignment-resolve-form" disabled={isPending || isLocked}>
                            <CheckCircleIcon />
                            Prześlij
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {assignment.available && (
                  <Badge variant="muted" border>
                    <ClockIcon />
                    Dostępne od {format(assignment.available, 'dd.MM.yyyy HH:mm')}
                  </Badge>
                )}
                {assignment.dueTo && (
                  <Badge variant="muted" border>
                    <CalendarIcon />
                    Termin {format(assignment.dueTo, 'dd.MM.yyyy HH:mm')}
                  </Badge>
                )}
                {!isQuizAssignment && (
                  <Badge variant="muted" border>
                    {podejsciaText}
                  </Badge>
                )}
              </div>

              {submitError && (
                <Alert variant="destructive" className="bg-destructive/5 border-border-destructive">
                  <AlertCircleIcon />
                  <AlertTitle>{submitError.title}</AlertTitle>
                  <AlertDescription>{submitError.detail}</AlertDescription>
                </Alert>
              )}

              {isLocked && (
                <Alert>
                  <AlertCircleIcon />
                  <AlertTitle>Brak dostępnych podejść</AlertTitle>
                  <AlertDescription>Wykorzystałeś już wszystkie dostępne podejścia.</AlertDescription>
                </Alert>
              )}
            </header>

            <div className="grid flex-1 grid-cols-1 gap-0 md:grid-cols-[minmax(0,1fr)_minmax(300px,340px)]">
              {/* Left column: description + (text-only) answer form */}
              <div className="md:border-border-primary w-full space-y-8 p-4 md:border-r md:border-b-0">
                {assignment.desc && (
                  <section className="space-y-2">
                    <h5 className="text-base font-semibold">Treść zadania</h5>
                    <Tiptap extensions={postExtensionSet} editable={false} content={assignment.desc} />
                  </section>
                )}

                {!isQuizAssignment && (
                  <>
                    <FormField
                      name="body"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>Twoja odpowiedź</FormLabel>
                          <div className="border-border-primary space-y-2 rounded-md border">
                            <FormControl>
                              <Tiptap
                                placeholder="Napisz swoją odpowiedź..."
                                extensions={simpleExtensionSet}
                                editable={!isLocked}
                                onUpdate={(res) => {
                                  if (res.editor.isEmpty) {
                                    field.onChange('');
                                    return;
                                  }
                                  field.onChange(res.editor.getHTML());
                                }}
                                content={field.value ?? ''}
                                className="min-h-32 p-1"
                              >
                                <TiptapToolbar className="-top-4 rounded-t-sm p-3" />
                              </Tiptap>
                            </FormControl>
                          </div>
                          <FormDescription className={cn('text-xs', bodyValue.length > 2000 && 'text-destructive')}>{bodyValue.length}/2000 znaków</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <section className="space-y-2">
                      <p className="text-muted-foreground text-xs font-medium uppercase">Załączniki ({attachments.length})</p>
                      {!isLocked && <FilesMultipleSelector onFilesChange={setAttachments} initialFiles={attachments} buttonSize="sm" buttonVariant="outline" className="bg-transparent" />}
                    </section>
                  </>
                )}
              </div>

              {/* Right column: quiz (if any) + teacher attachments */}
              <div className="space-y-8 p-4">
                {quiz && (
                  <section className="space-y-3">
                    <h5 className="text-base font-semibold">Quiz</h5>
                    <AssignmentBuilderQuizCard
                      quiz={quiz}
                      badge={
                        <Badge variant="muted" border>
                          Nie rozpoczęto
                        </Badge>
                      }
                      actions={
                        <Button type="button" variant="outline" size="sm" onClick={() => setStartDialog([true, quiz.id])}>
                          Rozpocznij
                          <ArrowUpRightIcon />
                        </Button>
                      }
                    />
                  </section>
                )}
                <section className="space-y-3">
                  <h5 className="text-base font-semibold">Pliki od nauczyciela</h5>
                  {assignment.files.length === 0 ? (
                    <p className="text-muted-foreground text-xs italic">Brak załączonych plików.</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {assignment.files.map((file) => (
                        <AttachmentCard key={file.id} id={file.id} name={file.name} />
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
